import { DateAdapter } from '@angular/material/core';

/** Adapts type `D` to be usable as a date by cdk-based components that work with dates. */
export abstract class DateTimeAdapter<D> extends DateAdapter<D> {
  /**
   * Gets the hours component of the given date.
   * @param date The date to extract the year from.
   * @returns The hours component.
   */
  abstract getHours(date: D): number;
  /**
   * Gets the hours component of the given date.
   * @param date The date to extract the year from.
   * @returns The hours component.
   */
  abstract getMinutes(date: D): number;
  /**
   * Gets the minutes component of the given date.
   * @param date The date to extract the minutes from.
   * @returns The minutes component.
   */
  abstract getSeconds(date: D): number;
  /**
   * Gets the seconds component of the given date.
   * @param date The date to extract the seconds from.
   * @returns The seconds component.
   */
  abstract getMilliseconds(date: D): number;
  /**
   *
   *
   * gets number of milliseconds since the beginning of the day
   * @param {D} date the date
   * @returns {number} the  number of milliseconds
   */
  abstract getZeroUnix(date: D): number;
  /**
   *
   *
   * sets the time components of the given date.
   * @param {D} date the date to set the time
   * @param {number} hh the hour part
   * @param {number} mm the minutes part
   * @param {number} ss the seconds part
   * @param {number} ms the milliseconds part
   * @returns {D} the new date
   */
  abstract setTime(date: D, hh: number | string, mm?: number | string, ss?: number | string, ms?: number | string): D;
  /**
   *
   *
   * @abstract
   * @param {D} date the date to set the time
   * @param {D} dateTime the date to get the time from
   * @returns {D}
   * @memberof DateTimeAdapter
   */
  abstract setTimeFromDate(date: D, dateTime: D): D;

  /**
   *
   * sets the hour components of the given date according to meridian.
   * @abstract
   * @param {D} date the date to set the meridian
   * @param {boolean} pm whether is PM(true) or AM(false)
   * @returns {D}
   */
  abstract setMeridian(date: D, pm: boolean): D;

  /**
   * Compares two dates.
   * @param first The first date to compare.
   * @param second The second date to compare.
   * @returns 0 if the dates are equal, a number less than 0 if the first date is earlier,
   *     a number greater than 0 if the first date is later.
   */
  abstract compareDateTime(first: D | null, second: D | null): number;
  /**
   * Checks if two datetimes are equal.
   * @param first The first datetime to check.
   * @param second The second datetime to check.
   * @returns Whether the two datetime are equal.
   *     Null dates are considered equal to other null dates.
   */
  abstract sameDateTime(first: D, second: D): boolean;

  /**
   * @param obj The object to check.
   * @returns The given object if it is both a date instance and valid, otherwise null.
   */
  abstract validOrNull(obj: any): D | null;
}
